import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { peopleApi } from '../api';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls =
  'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

// mode 'add': pick an active person + details. mode 'edit': person locked, edit details only.
export const AssignmentFormDialog = ({
  open,
  onOpenChange,
  mode = 'add',
  targetNoun = 'project',
  assignment,
  excludeKeys = [],
  onSubmit,
}) => {
  const [options, setOptions] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [personKey, setPersonKey] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'add') {
      setPersonKey('');
      setRoleLabel('');
      setIsPrimary(false);
      setNotes('');
      setLoadingPeople(true);
      peopleApi
        .assignees()
        .then((r) => setOptions(r.data))
        .catch(() => toast.error('Could not load people directory'))
        .finally(() => setLoadingPeople(false));
    } else if (assignment) {
      setRoleLabel(assignment.role_label || '');
      setIsPrimary(Boolean(assignment.is_primary));
      setNotes(assignment.internal_notes || '');
    }
  }, [open, mode, assignment]);

  const available = options.filter(
    (o) => !excludeKeys.includes(`${o.assignee_type}:${o.assignee_id}`)
  );
  const employees = available.filter((o) => o.assignee_type === 'employee');
  const subs = available.filter((o) => o.assignee_type === 'subcontractor');

  const handleSubmit = async () => {
    if (submitting) return;
    if (mode === 'add' && !personKey) {
      toast.error('Please select a person');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'add') {
        const [assignee_type, assignee_id] = personKey.split(':');
        const person = options.find(
          (o) => o.assignee_type === assignee_type && o.assignee_id === assignee_id
        );
        await onSubmit({
          assignee_type,
          assignee_id,
          display_name: person?.display_name,
          subtitle: person?.subtitle,
          role_label: roleLabel.trim() || null,
          is_primary: isPrimary,
          internal_notes: notes.trim() || null,
        });
      } else {
        await onSubmit({
          role_label: roleLabel.trim() || null,
          is_primary: isPrimary,
          internal_notes: notes.trim() || null,
        });
      }
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save assignment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-md"
        data-testid="assignment-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === 'add'
              ? targetNoun === 'project'
                ? 'Add Team Member'
                : 'Add Assignee'
              : 'Edit Assignment'}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {mode === 'add'
              ? 'Only active employees and subcontractors can be selected.'
              : assignment?.display_name || assignment?.assignee?.display_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {mode === 'add' && (
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Person *</label>
              {loadingPeople ? (
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading people…
                </div>
              ) : (
                <select
                  value={personKey}
                  onChange={(e) => setPersonKey(e.target.value)}
                  className={selectCls}
                  data-testid="af-person"
                >
                  <option value="">Select a person…</option>
                  {employees.length > 0 && (
                    <optgroup label="Employees">
                      {employees.map((o) => (
                        <option key={o.assignee_id} value={`employee:${o.assignee_id}`}>
                          {o.display_name}
                          {o.subtitle ? ` — ${o.subtitle}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {subs.length > 0 && (
                    <optgroup label="Subcontractors">
                      {subs.map((o) => (
                        <option key={o.assignee_id} value={`subcontractor:${o.assignee_id}`}>
                          {o.display_name}
                          {o.subtitle ? ` — ${o.subtitle}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              )}
              {!loadingPeople && available.length === 0 && (
                <p className="text-xs text-gray-500 mt-1.5">
                  No available active people. Add people in the People module first.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="text-sm text-gray-300 mb-1.5 block font-medium">
              {targetNoun === 'project' ? 'Project Role' : 'Role'}
            </label>
            <Input
              value={roleLabel}
              onChange={(e) => setRoleLabel(e.target.value)}
              placeholder={targetNoun === 'project' ? 'e.g., Project Manager' : 'e.g., Lead'}
              className={inputCls}
              data-testid="af-role"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              className="accent-[#D4AF37] h-4 w-4"
              data-testid="af-primary"
            />
            Primary {targetNoun === 'project' ? 'team member' : 'assignee'}
          </label>

          <div>
            <label className="text-sm text-gray-300 mb-1.5 block font-medium">
              Internal Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className={inputCls}
              data-testid="af-notes"
            />
            <p className="text-xs text-gray-600 mt-1">Internal only</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-3 border-t border-gray-800">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="border-gray-700 text-gray-300"
            data-testid="af-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (mode === 'add' && loadingPeople)}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
            data-testid="af-submit"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {mode === 'add' ? 'Add' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
